/**
 * Pure selectors for the Center Book sidebar dashboard.
 *
 * Each function takes a snapshot slice of the dashboard store and
 * returns a derived shape ready for rendering.  Kept out of the
 * component file so the math is unit-testable without React.
 *
 * Conventions:
 *   * Pod rows are filtered to ``risk_state != null`` (i.e. real pods
 *     from the building snapshot).  Cold-start pods with no events
 *     show up but with ``null`` metrics.
 *   * Numbers are in raw ₽ (or fractions for percentages); the
 *     component formats them.
 *   * Returns never include ``NaN`` -- a missing metric becomes
 *     ``null`` so React doesn't have to defend against it.
 */
import type { PodSummary } from '@/api/rest';
import type { AnyEnvelope } from '@/events';
import type { PodPosition, PodRuntimeState } from '@/state/store';

/** One pod's slot in the capital-allocation map.  ``share`` is normalized
 *  across all pods so the slice always sums to ~1.0. */
export interface PodCapitalSlot {
  podId: string;
  name: string;
  riskState: 'active' | 'watch' | 'reduced' | 'frozen';
  capital: number;
  share: number;
  dailyPnl: number | null;
  dailyReturn: number | null;
}

export interface CapitalMapResult {
  total: number;
  slots: PodCapitalSlot[];
}

/**
 * Capital allocation map across pods.  ``capital`` per pod comes from
 * the building snapshot (PodSummary.capital), which is the most
 * stable signal: live nav drifts every tick, the central capital
 * is updated only on ``centerbook.allocation_changed``.
 */
export function selectCapitalMap(
  building: ReadonlyArray<PodSummary>,
): CapitalMapResult {
  const slots: PodCapitalSlot[] = [];
  let total = 0;
  for (const pod of building) {
    const capital = pod.capital ?? 0;
    if (capital <= 0) continue;
    total += capital;
    slots.push({
      podId: pod.pod_id,
      name: pod.name,
      riskState: pod.risk_state,
      capital,
      share: 0, // filled below
      dailyPnl: pod.daily_pnl,
      dailyReturn: pod.daily_return,
    });
  }
  if (total > 0) {
    for (const s of slots) s.share = s.capital / total;
  }
  // Sort by capital descending so the biggest pod paints first.
  slots.sort((a, b) => b.capital - a.capital);
  return { total, slots };
}

export type RiskState = 'active' | 'watch' | 'reduced' | 'frozen';

export interface RiskStateBucket {
  state: RiskState;
  count: number;
  capital: number;
  podIds: string[];
}

export interface RiskStateBreakdown {
  totalCapital: number;
  buckets: Record<RiskState, RiskStateBucket>;
}

/**
 * Per-risk-state aggregation: how many pods + total capital in each
 * tier.  Order is fixed (active -> watch -> reduced -> frozen) so the
 * UI doesn't have to sort.
 */
export function selectRiskBreakdown(
  building: ReadonlyArray<PodSummary>,
): RiskStateBreakdown {
  const buckets: Record<RiskState, RiskStateBucket> = {
    active: { state: 'active', count: 0, capital: 0, podIds: [] },
    watch: { state: 'watch', count: 0, capital: 0, podIds: [] },
    reduced: { state: 'reduced', count: 0, capital: 0, podIds: [] },
    frozen: { state: 'frozen', count: 0, capital: 0, podIds: [] },
  };
  let totalCapital = 0;
  for (const pod of building) {
    const b = buckets[pod.risk_state];
    if (!b) continue;
    b.count += 1;
    b.capital += pod.capital ?? 0;
    b.podIds.push(pod.pod_id);
    totalCapital += pod.capital ?? 0;
  }
  return { totalCapital, buckets };
}

export interface TickerHolder {
  podId: string;
  podName: string;
  weight: number;
  /** Absolute weight (= |weight|), used to rank holders by exposure. */
  exposure: number;
  notional: number | null;
  side: 'long' | 'short';
}

export interface TickerConcentration {
  ticker: string;
  holders: TickerHolder[];
  totalNotional: number;
  /** Long-only notional sum (positive). */
  longNotional: number;
  /** Short notional magnitude (positive). */
  shortNotional: number;
  /** True when any pod is long AND another is short the same ticker --
   *  the platform is internally hedged on this name, which is usually
   *  fine but worth flagging in the heatmap.  */
  netted: boolean;
}

/**
 * Tickers held by 2+ pods, ranked by holder count then total
 * notional.  ``limit`` caps the result so a portfolio with 200
 * tickers stays renderable.  Returns empty when no broker snapshot
 * has arrived yet.
 */
export function selectTickerConcentration(
  pods: Record<string, PodRuntimeState | undefined>,
  podNames: Record<string, string>,
  limit = 8,
): TickerConcentration[] {
  // Map: ticker → list of holders
  const byTicker = new Map<string, TickerHolder[]>();
  for (const pod of Object.values(pods)) {
    if (!pod) continue;
    const positions: Record<string, PodPosition> = pod.positions ?? {};
    for (const p of Object.values(positions)) {
      const weight = p.weight ?? 0;
      if (weight === 0) continue;
      const ticker = (p.ticker ?? '').toUpperCase();
      if (!ticker) continue;
      const notional = computeNotional(p);
      const list = byTicker.get(ticker) ?? [];
      list.push({
        podId: pod.podId,
        podName: podNames[pod.podId] ?? pod.podId,
        weight,
        exposure: Math.abs(weight),
        notional,
        // Prefer the broker-supplied side when present; fall back to
        // weight sign so weight-only stream events still classify.
        side: p.side ?? (weight >= 0 ? 'long' : 'short'),
      });
      byTicker.set(ticker, list);
    }
  }
  const out: TickerConcentration[] = [];
  for (const [ticker, holders] of byTicker) {
    if (holders.length < 2) continue;
    let longNotional = 0;
    let shortNotional = 0;
    let hasLong = false;
    let hasShort = false;
    for (const h of holders) {
      if (h.notional == null) continue;
      if (h.side === 'long') {
        longNotional += h.notional;
        hasLong = true;
      } else {
        shortNotional += Math.abs(h.notional);
        hasShort = true;
      }
    }
    out.push({
      ticker,
      holders: holders.sort((a, b) => b.exposure - a.exposure),
      totalNotional: longNotional + shortNotional,
      longNotional,
      shortNotional,
      netted: hasLong && hasShort,
    });
  }
  // Sort: holder count desc, then |notional| desc.
  out.sort((a, b) => {
    if (b.holders.length !== a.holders.length) {
      return b.holders.length - a.holders.length;
    }
    return b.totalNotional - a.totalNotional;
  });
  return out.slice(0, limit);
}

function computeNotional(p: PodPosition): number | null {
  // Prefer the broker-supplied notional -- it's already signed and
  // FX-converted by backend.  Fall back to qty × price when broker
  // snapshot hasn't landed (weight-only stream).
  if (p.notional != null) return p.notional;
  if (p.qty == null || p.price == null) return null;
  return p.qty * p.price;
}

export interface PodPnlRow {
  podId: string;
  name: string;
  dailyPnl: number;
  dailyReturn: number | null;
  capital: number;
  riskState: RiskState;
}

export interface PnlDistribution {
  rows: PodPnlRow[];
  aggregateNet: number;
  maxAbs: number;
}

/**
 * Daily PnL distribution across pods, sorted descending.  ``maxAbs``
 * is the largest absolute PnL across pods; used to scale bar lengths
 * so the biggest mover always touches the edge of its track.
 */
export function selectPnlDistribution(
  building: ReadonlyArray<PodSummary>,
): PnlDistribution {
  const rows: PodPnlRow[] = [];
  let aggregateNet = 0;
  let maxAbs = 0;
  for (const pod of building) {
    const pnl = pod.daily_pnl;
    if (pnl == null) continue;
    rows.push({
      podId: pod.pod_id,
      name: pod.name,
      dailyPnl: pnl,
      dailyReturn: pod.daily_return,
      capital: pod.capital ?? 0,
      riskState: pod.risk_state,
    });
    aggregateNet += pnl;
    if (Math.abs(pnl) > maxAbs) maxAbs = Math.abs(pnl);
  }
  rows.sort((a, b) => b.dailyPnl - a.dailyPnl);
  return { rows, aggregateNet, maxAbs };
}

export interface AlertsRollupRow {
  podId: string;
  name: string;
  errorCount: number;
  lastKind: string | null;
  lastReason: string | null;
  lastTicker: string | null;
}

export interface AlertsRollup {
  totalErrors: number;
  rows: AlertsRollupRow[];
}

/**
 * Per-pod alert tally + the most recent error reason.  ``rows`` are
 * filtered to pods with at least one alert; sorted by errorCount
 * descending.  Empty when no pod has flagged an error in the current
 * session.
 */
export function selectAlertsRollup(
  pods: Record<string, PodRuntimeState | undefined>,
  podNames: Record<string, string>,
): AlertsRollup {
  const rows: AlertsRollupRow[] = [];
  let totalErrors = 0;
  for (const pod of Object.values(pods)) {
    if (!pod) continue;
    const count = pod.alerts?.errorCount ?? 0;
    if (count === 0) continue;
    totalErrors += count;
    const last = pod.alerts?.last ?? null;
    rows.push({
      podId: pod.podId,
      name: podNames[pod.podId] ?? pod.podId,
      errorCount: count,
      lastKind: last?.kind ?? null,
      lastReason: last?.reason ?? null,
      lastTicker: last?.ticker ?? null,
    });
  }
  rows.sort((a, b) => b.errorCount - a.errorCount);
  return { totalErrors, rows };
}

// =================================================================
// Correlation matrix (position-overlap, cosine similarity)
// =================================================================

export interface CorrelationCell {
  /** Cosine similarity in ``[-1, 1]``.  ``null`` when either pod has
   *  no positions -- we can't compare an empty vector. */
  correlation: number | null;
  /** Tickers that both pods hold, ranked by ``min(|w_a|, |w_b|)``
   *  (the overlap contribution).  Used by the tooltip to explain
   *  *why* the correlation is what it is. */
  sharedTickers: ReadonlyArray<{
    ticker: string;
    weightA: number;
    weightB: number;
  }>;
}

export interface CorrelationMatrix {
  /** Ordered list of pod ids -- both axes of the matrix.  Same order
   *  as the input ``podIds`` so the caller controls layout. */
  podIds: string[];
  /** ``cells[i][j]`` = correlation between pods[i] and pods[j].  The
   *  matrix is symmetric and diagonal cells have correlation 1. */
  cells: CorrelationCell[][];
  /** Highest off-diagonal correlation observed.  ``null`` when fewer
   *  than two pods have positions. */
  maxCorrelation: number | null;
  /** Pairs sorted by absolute correlation descending, truncated to
   *  the top ``MAX_PAIRS`` so the UI can render a compact list view.
   *  Only off-diagonal, non-null pairs. */
  topPairs: ReadonlyArray<{
    podA: string;
    podB: string;
    correlation: number;
    sharedTickers: CorrelationCell['sharedTickers'];
  }>;
}

const MAX_PAIRS = 10;

/**
 * Cosine similarity between two pods' weight vectors, aligned by
 * ticker.  Sign-aware: identical longs ⇒ +1, identical shorts ⇒ +1,
 * one long & one short the same ticker ⇒ -1.  Zero ticker overlap
 * ⇒ 0.  Returns ``null`` when either pod has no positions.
 */
function positionCosine(
  a: Record<string, PodPosition>,
  b: Record<string, PodPosition>,
): { corr: number | null; shared: CorrelationCell['sharedTickers'] } {
  const aKeys = Object.keys(a);
  const bKeys = Object.keys(b);
  if (aKeys.length === 0 || bKeys.length === 0) {
    return { corr: null, shared: [] };
  }
  // Union of tickers; zero on the side that doesn't hold it.
  const tickers = new Set<string>([...aKeys, ...bKeys]);
  let dot = 0;
  let magA = 0;
  let magB = 0;
  const shared: { ticker: string; weightA: number; weightB: number }[] = [];
  for (const t of tickers) {
    const wA = a[t]?.weight ?? 0;
    const wB = b[t]?.weight ?? 0;
    dot += wA * wB;
    magA += wA * wA;
    magB += wB * wB;
    if (wA !== 0 && wB !== 0) {
      shared.push({ ticker: t, weightA: wA, weightB: wB });
    }
  }
  if (magA === 0 || magB === 0) {
    return { corr: null, shared: [] };
  }
  let corr = dot / (Math.sqrt(magA) * Math.sqrt(magB));
  // Clamp against FP noise.
  if (corr > 1) corr = 1;
  if (corr < -1) corr = -1;
  // Rank shared tickers by minimum-side magnitude -- the
  // smaller-side weight is the binding contribution.
  shared.sort(
    (x, y) =>
      Math.min(Math.abs(y.weightA), Math.abs(y.weightB)) -
      Math.min(Math.abs(x.weightA), Math.abs(x.weightB)),
  );
  return { corr, shared };
}

/**
 * Pairwise cosine-similarity matrix across pods' position vectors.
 * Symmetric; diagonal = 1.  Off-diagonal cells with ``null``
 * correlation mean one side had no positions (cold-start pod).
 *
 * ``podIds`` controls row/column order so the caller can render an
 * axis-aligned grid with stable cell positions across renders.
 */
export function selectCorrelationMatrix(
  podIds: ReadonlyArray<string>,
  pods: Record<string, PodRuntimeState | undefined>,
): CorrelationMatrix {
  const cells: CorrelationCell[][] = [];
  const topPairs: Array<{
    podA: string;
    podB: string;
    correlation: number;
    sharedTickers: CorrelationCell['sharedTickers'];
  }> = [];
  let maxCorrelation: number | null = null;
  for (let i = 0; i < podIds.length; i++) {
    const row: CorrelationCell[] = [];
    const a = pods[podIds[i]!]?.positions ?? {};
    for (let j = 0; j < podIds.length; j++) {
      if (i === j) {
        row.push({ correlation: 1, sharedTickers: [] });
        continue;
      }
      // Avoid recomputing -- the matrix is symmetric.
      if (j < i) {
        const mirror = cells[j]?.[i] ?? {
          correlation: null,
          sharedTickers: [],
        };
        row.push(mirror);
        continue;
      }
      const b = pods[podIds[j]!]?.positions ?? {};
      const { corr, shared } = positionCosine(a, b);
      row.push({ correlation: corr, sharedTickers: shared });
      if (corr !== null) {
        if (maxCorrelation === null || corr > maxCorrelation) {
          maxCorrelation = corr;
        }
        topPairs.push({
          podA: podIds[i]!,
          podB: podIds[j]!,
          correlation: corr,
          sharedTickers: shared,
        });
      }
    }
    cells.push(row);
  }
  topPairs.sort((x, y) => Math.abs(y.correlation) - Math.abs(x.correlation));
  return {
    podIds: [...podIds],
    cells,
    maxCorrelation,
    topPairs: topPairs.slice(0, MAX_PAIRS),
  };
}

// =================================================================
// Capital flow timeline (centerbook.allocation_changed history)
// =================================================================

export interface CapitalFlowEvent {
  podId: string;
  podName: string;
  /** ms-since-epoch -- ``Date.parse(env.ts)``. */
  atMs: number;
  capitalBefore: number | null;
  capitalAfter: number;
  /** Fraction.  ``+0.30`` = +30 %.  ``null`` when before is unknown. */
  deltaPct: number | null;
  reason: string | null;
}

const CAPITAL_FLOW_LIMIT = 12;

/**
 * Walk the persisted feed and pluck every
 * ``centerbook.allocation_changed`` event, newest first.  Bounded at
 * ``CAPITAL_FLOW_LIMIT`` so a heavy reallocation day stays render-
 * friendly.  Falls back gracefully on malformed payloads (skip).
 */
export function selectCapitalFlowEvents(
  feed: ReadonlyArray<AnyEnvelope>,
  podNames: Record<string, string>,
): CapitalFlowEvent[] {
  const out: CapitalFlowEvent[] = [];
  // Walk from newest (end) to oldest -- bounded scan keeps us fast
  // even on a 200-envelope ring.
  for (let i = feed.length - 1; i >= 0 && out.length < CAPITAL_FLOW_LIMIT; i--) {
    const env = feed[i];
    if (!env || env.type !== 'centerbook.allocation_changed') continue;
    const d = env.data;
    const capitalAfter =
      typeof d.capital_after === 'number'
        ? d.capital_after
        : typeof d.new_capital === 'number'
          ? d.new_capital
          : null;
    if (capitalAfter == null) continue;
    const capitalBefore =
      typeof d.capital_before === 'number'
        ? d.capital_before
        : typeof d.old_capital === 'number'
          ? d.old_capital
          : null;
    const deltaPct =
      typeof d.delta_pct === 'number'
        ? d.delta_pct
        : capitalBefore != null && capitalBefore > 0
          ? (capitalAfter - capitalBefore) / capitalBefore
          : null;
    const podId =
      typeof d.pod_id === 'string'
        ? d.pod_id
        : env.subject?.startsWith('pod:')
          ? env.subject.slice(4)
          : '';
    if (!podId) continue;
    out.push({
      podId,
      podName: podNames[podId] ?? podId,
      atMs: Date.parse(env.ts) || 0,
      capitalBefore,
      capitalAfter,
      deltaPct,
      reason: typeof d.reason === 'string' ? d.reason : null,
    });
  }
  return out;
}
